<script setup lang="ts">
import { CirclePlus, Fold } from "@element-plus/icons-vue";
import { computed, onMounted, ref } from "vue";
import {
  SysOrgRoleName,
  WorkflowDefinition,
  WorkflowDefinitionListDto,
} from "~/api";
import WorkflowDefinitionEditor from "~/Compliance/components/WorkflowDefinitionEditor.vue";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { Slotted } from "~/types/Slotted";
import { apiClient } from "~/utils/apiClient";

type listItemType = WorkflowDefinitionListDto;
type mainType = WorkflowDefinition;
const loading = ref(true);

const items = ref<listItemType[]>([]);
const selectedRow = ref<listItemType | undefined>({} as listItemType);
const drawerOpen = ref(false);
const myInfoStore = useMyInfoStore();

const hasSetupBasicRole = myInfoStore.currentSysOrg.roles.includes(
  SysOrgRoleName.SETUP_BASIC,
);

const refreshList = async () => {
  loading.value = true;
  apiClient.workflowDefinition
    .getAllWorkflowDefinitionDtos(myInfoStore.currentSysOrgGuid)
    .then((res) => {
      items.value = res;
      sortList();
      loading.value = false;
    });
};

onMounted(async () => {
  refreshList();
});

const extractListItem = (item: mainType): listItemType => {
  // similar to creating the DTO on the server
  return {
    guid: item.guid,
    name: item.name ?? "",
    // formDefinitionGuid: item.formDefinitions?.length
    //   ? item.formDefinitions[0].guid
    //   : "",
    updatedAt: item.updatedAt ?? "",
  };
};

const sortList = () => {
  items.value.sort((a: listItemType, b: listItemType) => {
    return (a.name ?? "").localeCompare(b.name ?? "");
  });
};

let numCreated = 0;
const createItem = () => {
  selectedRow.value = { guid: "new" + numCreated++ } as listItemType;
  drawerOpen.value = true;
};

/// Show the clicked item in the drawer
const showItem = (row: listItemType) => {
  selectedRow.value = row;
  drawerOpen.value = true;
};

const handleNewItemAdded = (item: mainType) => {
  items.value.push(extractListItem(item));
  selectedRow.value = extractListItem(item);
  sortList();
};

const handleItemUpdated = (item: mainType) => {
  const index = items.value.findIndex((f) => f.guid === item.guid);
  if (index !== -1) {
    items.value[index] = extractListItem(item);
  }
  sortList();
};

const handleItemDeleted = (item: mainType) => {
  const index = items.value.findIndex((f) => f.guid === item.guid);
  if (index !== -1) {
    items.value.splice(index, 1);
  }
};

const closeDrawer = () => {
  drawerOpen.value = false;
  selectedRow.value = undefined;
};

const itemsFiltered = computed(() => {
  // custom per item types
  return items.value;
});
</script>

<template>
  <div class="flexRow">
    <span class="showingInTable">
      Showing {{ itemsFiltered.length }} of
      {{ items.length }}
    </span>
    <RefreshButton @click="refreshList" />
    <!-- <ComplianceQuery @submit:query="complianceQueryInput = $event">
      <template #create-button> </template>
    </ComplianceQuery> -->
    <el-button
      v-if="hasSetupBasicRole"
      class="addButton"
      :icon="CirclePlus"
      title="Create a new workflow definition"
      type="primary"
      style="font-size: 15px"
      @click="createItem"
    >
      New Workflow
    </el-button>
  </div>

  <el-table v-loading="loading" :data="itemsFiltered" style="width: 100%">
    <el-table-column prop="name" label="Workflow Name">
      <template #default="{ row }: Slotted<listItemType>">
        <el-link :icon="Fold" underline="never" @click="showItem(row)">
          {{ row.name }}
        </el-link>
      </template>
    </el-table-column>
  </el-table>

  <WorkflowDefinitionEditor
    v-if="selectedRow?.guid"
    :key="selectedRow.guid"
    :workflow-definition-list-dto="selectedRow"
    :drawer-open="drawerOpen"
    :model-value="drawerOpen"
    @close-drawer="closeDrawer"
    @added="handleNewItemAdded"
    @updated="handleItemUpdated"
    @deleted="handleItemDeleted"
  >
  </WorkflowDefinitionEditor>
</template>

<style lang="less"></style>
